import { useTranslation } from "react-i18next";
import Currency from "@enums/currency";
import { SceneStock as SceneStockType } from "@ltypes/scene";
import DisplayForm from "@components/forms/DisplayForm/DisplayForm";
import List, { ListItemType } from "@components/forms/List/List";
import Select from "@components/forms/Select/Select";
import TextField from "@components/forms/TextField/TextField";
import { useSelector } from "react-redux";
import Product, { ProductModelType } from "@ltypes/product";
import { displayModes } from "../../../constants";
import Coin from "@ltypes/asset";
import Switch from "@components/forms/Switch/Switch";
import Message from "@components/ui/Message/Message";

type SceneStockProps = {
    scene: SceneStockType,
    onChange: (field: string, value: any) => void,
};

function SceneStock(props: SceneStockProps) {

    const { t } = useTranslation();
    const product: Product = useSelector((state: any) => state.product);
    const stocksList: Array<Coin> = useSelector((state: any) => state.stock);

    const stocksAvailable: Array<ListItemType> = stocksList.map((coin: Coin) => ({
        label: coin.symbol,
        value: coin.symbol,
        description: coin.name,
        disabled: !coin.active,
    }));

    const getCurrencyLabel = (currency: string) => {
        if (currency in Currency) {
            return Currency[currency as keyof typeof Currency];
        }
        return '???';
    };

    return (
        <div className="row g-3">
            <div className="col-12 col-md-6">
                <div className="row g-3">
                    <div className="col-12">
                        <DisplayForm label={t('page.scenes.data.display_mode')} value={props.scene.display_mode} items={displayModes[product.model.type].stock.map((index: number) => ({
                            id: index,
                            background: `stock-${index}`,
                        }))} productModelType={product.model.type} onChange={(value: number) => props.onChange('display_mode', value)}/>
                    </div>
                    <div className="col-12">
                        <TextField label={product.model.type === ProductModelType.LEGACY || ![1,2].includes(props.scene.display_mode) ? t('page.scenes.data.display_duration_stock') : t('page.scenes.data.display_duration')} value={props.scene.display_duration} onChange={(value: string) => props.onChange('display_duration', value)}/>
                    </div>
                    <div className="col-12">
                        <Select label={t('page.scenes.data.currency')} value={props.scene.currency} items={Object.keys(Currency).map((currency: string) => ({
                            label: getCurrencyLabel(currency),
                            value: currency,
                        }))} onChange={(value: string) => props.onChange('currency', value)}/>
                    </div>
                    {product.model.type === ProductModelType.STANDARD && props.scene.display_mode === 3 && (
                        <div className="col-12">
                            <Select label={t('page.scenes.data.candlestick_interval')} value={props.scene.interval ?? 15} items={[15,30,60].map((interval: number) => ({
                                label: `${interval}min`,
                                value: interval,
                            }))} onChange={(value: string) => props.onChange('interval', value)}/>
                        </div>
                    )}
                </div>
            </div>
            <div className="col-12 col-md-6">
                <div className="row g-3">
                    <div className="col-12 col-md-8">
                        <List label={t('page.scenes.data.stocks')} values={props.scene.stocks ?? []} items={stocksAvailable} iconPath={`${process.env.REACT_APP_API_URL}/media/stocks/`} onChange={(values: Array<string>) => props.onChange('stocks', values)}/>
                    </div>
                    {[0, 2, 3].includes(props.scene.display_mode) && (
                        <>
                            <div className="col-12">
                                <Switch label={t('page.scenes.data.show_market_status')} checked={props.scene.show_market_status} onChange={(checked: boolean) => props.onChange('show_market_status', Boolean(checked))}/>
                            </div>
                            <div className="col-12">
                                <Switch label={t('page.scenes.data.show_session_marker')} checked={props.scene.show_session_marker} onChange={(checked: boolean) => props.onChange('show_session_marker', Boolean(checked))}/>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="col-12">
                <Message type="information">
                    {t('message.us_market_hours')}
                </Message>
            </div>
        </div>
    );
}

export default SceneStock;
